import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS } from "@/data/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const close = () => setOpen(false);

  return (
    <header className="sticky top-4 z-50 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="backdrop-blur-xl bg-white/80 border border-gray-200/80 rounded-2xl shadow-sm">
        <div className="flex items-center justify-between px-4 sm:px-5 py-3">
          <Logo />
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-ink text-white" : "text-gray-600 hover:text-ink hover:bg-gray-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link
              to="/contact"
              data-testid="header-cta-quote"
              className="hidden md:inline-flex items-center gap-1.5 bg-cobalt hover:bg-cobalt-hover text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-200"
            >
              Get a Quote
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.nav
              key={location.pathname + "-mobile"}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden overflow-hidden border-t border-gray-200/80"
              data-testid="mobile-nav"
            >
              <div className="px-4 py-3 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={close}
                    data-testid={`mobile-${l.testid}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                        isActive ? "bg-ink text-white" : "text-gray-700 hover:bg-gray-100"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
                <Link
                  to="/contact"
                  onClick={close}
                  data-testid="mobile-header-cta-quote"
                  className="mt-2 inline-flex items-center justify-center gap-1.5 bg-cobalt text-white text-sm font-semibold px-5 py-3 rounded-xl"
                >
                  Get a Quote
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
